import moment from 'moment';

const MomentPlugin = {
    install(app, options = {}) {
        // Default formats
        const defaultFormats = {
            date: 'YYYY-MM-DD',
            datetime: 'YYYY-MM-DD HH:mm:ss',
            displayDate: 'DD MMM YYYY',
            displayDateTime: 'DD MMM YYYY, hh:mm A',
            iso: 'YYYY-MM-DDTHH:mm:ssZ',
            ...options.formats
        };

        // Safe moment wrapper with error handling
        const safeMoment = (value) => {
            if (!value) return null;
            try {
                return moment(value);
            } catch (error) {
                console.warn('Invalid date value:', value, error);
                return null;
            }
        };

        // Format date method
        const formatDate = (value, format = 'displayDate') => {
            const m = safeMoment(value);
            if (!m || !m.isValid()) {
                return options.invalidText || '--';
            }

            const formatStr = defaultFormats[format] || format;
            return m.format(formatStr);
        };

        // From now (relative time)
        const fromNow = (value) => {
            const m = safeMoment(value);
            if (!m || !m.isValid()) {
                return options.invalidText || '--';
            }
            return m.fromNow();
        };

        // Format date for business cards
        const formatBusinessDate = (value) => {
            const m = safeMoment(value);
            if (!m || !m.isValid()) return '--';

            const now = moment();
            const diffDays = now.diff(m, 'days');

            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

            return m.format('DD MMM YYYY');
        };

        // Add to global properties
        app.config.globalProperties.$moment = moment;
        app.config.globalProperties.$formatDate = formatDate;
        app.config.globalProperties.$fromNow = fromNow;
        app.config.globalProperties.$momentFormats = defaultFormats;

        // Add helper object
        app.config.globalProperties.$date = {
            format: formatDate,
            fromNow: fromNow,
            formatBusinessDate: formatBusinessDate,
            isValid: (value) => {
                const m = safeMoment(value);
                return m && m.isValid();
            },
            diff: (date1, date2, unit = 'days') => {
                const m1 = safeMoment(date1);
                const m2 = safeMoment(date2);
                if (!m1 || !m2) return 0;
                return m1.diff(m2, unit);
            },
            add: (date, amount, unit = 'days') => {
                const m = safeMoment(date);
                if (!m) return null;
                return m.add(amount, unit);
            },
            subtract: (date, amount, unit = 'days') => {
                const m = safeMoment(date);
                if (!m) return null;
                return m.subtract(amount, unit);
            },
            startOf: (date, unit = 'day') => {
                const m = safeMoment(date);
                if (!m) return null;
                return m.startOf(unit);
            },
            endOf: (date, unit = 'day') => {
                const m = safeMoment(date);
                if (!m) return null;
                return m.endOf(unit);
            }
        };
    }
};

// Export moment instance for direct imports
export { moment };

// Export the plugin
export default MomentPlugin;